export type ErrorLevel =
  | "fatal"
  | "error"
  | "warning"
  | "log"
  | "info"
  | "debug";

export type CaptureTags = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface CaptureErrorContext {
  tags?: CaptureTags;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
  level?: ErrorLevel;
  contexts?: Record<string, Record<string, unknown>>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isCaptureErrorContext(value: unknown): value is CaptureErrorContext {
  if (!isRecord(value)) return false;
  return (
    "tags" in value ||
    "extra" in value ||
    "fingerprint" in value ||
    "level" in value ||
    "contexts" in value
  );
}

export function toCaptureErrorContext(
  context?: CaptureErrorContext | Record<string, unknown>,
): CaptureErrorContext {
  if (!isCaptureErrorContext(context)) {
    return { extra: context };
  }

  const { tags, extra, fingerprint, level, contexts, ...legacyExtra } = context;

  return {
    tags,
    extra: {
      ...(isRecord(extra) ? extra : {}),
      ...legacyExtra,
    },
    fingerprint,
    level,
    contexts,
  };
}

export function stringifyTags(tags?: CaptureTags): Record<string, string> {
  if (!tags) return {};

  return Object.fromEntries(
    Object.entries(tags).flatMap(([key, value]) =>
      value === undefined || value === null ? [] : [[key, String(value)]],
    ),
  );
}

function normalizeRecordError(error: Record<string, unknown>): {
  error: Error;
  extra: Record<string, unknown>;
} {
  const message =
    typeof error.message === "string"
      ? error.message
      : "Non-Error exception captured";
  const normalizedError = new Error(message);

  if (typeof error.name === "string" && error.name.length > 0) {
    normalizedError.name = error.name;
  }

  const digest =
    typeof error.digest === "string" && error.digest.length > 0
      ? error.digest
      : undefined;

  return {
    error: normalizedError,
    extra: {
      originalErrorType: "object",
      originalErrorValue: error,
      ...(digest ? { digest } : {}),
    },
  };
}

export function normalizeUnknownError(error: unknown): {
  error: Error;
  extra: Record<string, unknown>;
} {
  if (error instanceof Error) {
    const digest =
      "digest" in error && typeof error.digest === "string"
        ? error.digest
        : undefined;

    return { error, extra: digest ? { digest } : {} };
  }

  if (typeof error === "string") {
    return {
      error: new Error(error),
      extra: { originalErrorType: "string", originalErrorValue: error },
    };
  }

  if (isRecord(error)) {
    return normalizeRecordError(error);
  }

  return {
    error: new Error("Non-Error exception captured"),
    extra: {
      originalErrorType: typeof error,
      originalErrorValue: error,
    },
  };
}

export function toCaptureProperties(
  context: CaptureErrorContext,
  baseExtra: Record<string, unknown>,
): Record<string, unknown> {
  const contexts = context.contexts ?? {};

  return {
    ...stringifyTags(context.tags),
    ...baseExtra,
    ...(context.extra ?? {}),
    ...Object.fromEntries(
      Object.entries(contexts).map(([key, value]) => [`context_${key}`, value]),
    ),
    ...(context.level ? { $exception_level: context.level } : {}),
    ...(context.fingerprint
      ? { $exception_fingerprint: context.fingerprint.join(" | ") }
      : {}),
  };
}
